/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { NET_DAYS_OPTIONS } from '@therapy/shared-validators';
import { format } from 'date-fns';
import { type ChangeEvent, useCallback, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { type Proforma } from '../../lib/api/proformas.js';
import { useBulkConvertProformas, useConvertProforma } from '../../lib/queries/proformas.js';

type NetDays = (typeof NET_DAYS_OPTIONS)[number];

const INPUT =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const LABEL = 'mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300';

/** Shared modal that collects the invoice date + net payment term for a conversion. */
function ConvertModal({
  open,
  pending,
  onConfirm,
  onClose,
}: {
  readonly open: boolean;
  readonly pending: boolean;
  readonly onConfirm: (invoiceDate: string, netDays: NetDays) => void;
  readonly onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [date, setDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [net, setNet] = useState<NetDays>(15);
  const onDate = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setDate(e.target.value);
  }, []);
  const onNet = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setNet(Number(e.target.value) as NetDays);
  }, []);
  const onModal = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        onClose();
      }
    },
    [onClose],
  );
  const confirm = useCallback(() => {
    onConfirm(date, net);
  }, [onConfirm, date, net]);

  return (
    <Modal
      open={open}
      onOpenChange={onModal}
      title={t('proforma.convertTitle')}
      closeLabel={t('common.cancel')}
    >
      <div className="space-y-3">
        <div>
          <label htmlFor="cv-date" className={LABEL}>
            {t('proforma.invoiceDate')}
          </label>
          <input id="cv-date" type="date" className={INPUT} value={date} onChange={onDate} />
        </div>
        <div>
          <label htmlFor="cv-net" className={LABEL}>
            {t('proforma.netTerm')}
          </label>
          <select id="cv-net" className={INPUT} value={net} onChange={onNet}>
            {NET_DAYS_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {t('proforma.netDays', { count: n })}
              </option>
            ))}
          </select>
        </div>
        <div className="flex justify-end">
          <Button type="button" onClick={confirm} disabled={pending}>
            {t('proforma.convert')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Per-card "Convert to invoice" action shown on an approved proforma. */
export function ApprovedActions({ proforma }: { readonly proforma: Proforma }): JSX.Element {
  const { t } = useTranslation();
  const convert = useConvertProforma();
  const [open, setOpen] = useState(false);
  const onOpen = useCallback(() => {
    setOpen(true);
  }, []);
  const onClose = useCallback(() => {
    setOpen(false);
  }, []);
  const onConfirm = useCallback(
    (invoiceDate: string, netDays: NetDays) => {
      convert.mutate({ id: proforma.id, input: { invoiceDate, netDays } }, { onSuccess: onClose });
    },
    [convert, proforma.id, onClose],
  );
  return (
    <>
      <Button type="button" onClick={onOpen}>
        {t('proforma.convert')}
      </Button>
      <ConvertModal
        open={open}
        pending={convert.isPending}
        onConfirm={onConfirm}
        onClose={onClose}
      />
    </>
  );
}

/** Header action that bulk-converts every approved proforma in one go. */
export function ConvertApprovedButton({
  proformas,
}: {
  readonly proformas: readonly Proforma[];
}): JSX.Element | null {
  const { t } = useTranslation();
  const bulk = useBulkConvertProformas();
  const [open, setOpen] = useState(false);
  const ids = useMemo(
    () => proformas.filter((p) => p.status === 'approved').map((p) => p.id),
    [proformas],
  );
  const onOpen = useCallback(() => {
    setOpen(true);
  }, []);
  const onClose = useCallback(() => {
    setOpen(false);
  }, []);
  const onConfirm = useCallback(
    (invoiceDate: string, netDays: NetDays) => {
      bulk.mutate({ ids, invoiceDate, netDays }, { onSuccess: onClose });
    },
    [bulk, ids, onClose],
  );
  if (ids.length === 0) {
    return null;
  }
  return (
    <>
      <Button type="button" variant="outline" onClick={onOpen}>
        {t('proforma.convertApproved', { count: ids.length })}
      </Button>
      <ConvertModal open={open} pending={bulk.isPending} onConfirm={onConfirm} onClose={onClose} />
    </>
  );
}
